import React from 'react';
import type { StatusDialProps } from './types';
import { getStatusColor, getStatusLabel, ExtendedStatusValue } from '../../../utils/statusColors';
import './StatusDial.scss';

/**
 * Status sparkle icon SVG component
 */
interface StatusIconProps {
  color: string;
  size?: number;
}

const StatusIcon: React.FC<StatusIconProps> = ({ color, size = 24 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 27 26"
    fill="none"
    aria-hidden="true"
    className="status-dial__icon"
  >
    <g clipPath="url(#status-dial-clip)">
      <path
        d="M6.72039 12.8585L11.5481 10.986L13.3037 5.83639C13.4867 5.29933 14.1988 5.29933 14.3819 5.83639L16.1374 10.986L20.9651 12.8585C21.4687 13.0538 21.4687 13.8134 20.9651 14.0087L16.1374 15.8812L14.3819 21.0308C14.1988 21.5679 13.4867 21.5679 13.3037 21.0308L11.5481 15.8812L6.72039 14.0087C6.2169 13.8134 6.2169 13.0538 6.72039 12.8585Z"
        fill={color}
        stroke={color}
      />
    </g>
    <defs>
      <clipPath id="status-dial-clip">
        <rect width="26" height="26" fill="white" transform="translate(26.5) rotate(90)" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Dial component that displays the sphere's status in the center.
 * Shows a status sparkle icon with the status label.
 */
const StatusDial: React.FC<StatusDialProps> = ({
  sphere,
  onClick,
}) => {
  const status = (sphere?.status?.currentValue?.status || 'unknown') as ExtendedStatusValue;

  const statusColor = getStatusColor(status);
  const statusLabel = getStatusLabel(status);

  // Get velocity info if available
  const velocity = sphere?.status?.currentValue?.velocity;
  const velocityBucket = velocity?.bucket;

  const velocityArrow = (() => {
    if (velocity?.direction === 'improving') return '\u2191';
    if (velocity?.direction === 'declining') return '\u2193';
    return '\u2192';
  })();

  return (
    <button
      type="button"
      className="status-dial"
      onClick={onClick}
    >
      {/* Status sparkle icon */}
      <StatusIcon color={statusColor} size={32} />

      {/* Status label */}
      <div
        className="status-dial__value"
        style={{ color: statusColor }}
      >
        {statusLabel}
      </div>

      {/* Velocity indicator */}
      {velocityBucket && (
        <div className="status-dial__velocity">
          <span className={`status-dial__velocity-arrow status-dial__velocity-arrow--${velocity?.direction || 'stable'}`}>
            {velocityArrow}
          </span>
          <span className="status-dial__velocity-label">
            {velocityBucket.replace('_', ' ')}
          </span>
        </div>
      )}
    </button>
  );
};

export default StatusDial;
